import { NgClass, NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonStyle } from 'app/shared/enums';
import { getFileSize } from 'app/shared/helpers';
import { BlobUploadService } from 'app/shared/services/blob-upload.service';
import { CustomSnackbarComponent } from '../../molecules/custom-snackbar/custom-snackbar.component';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styles: [],
  imports: [NgIf, NgClass, MatIcon, ButtonComponent, TranslateModule],
})
export class UploadComponent implements OnInit {
  protected readonly blobUploadService = inject(BlobUploadService);
  protected readonly snackBar = inject(MatSnackBar);

  @Input() public label = '';
  @Input() public fileName!: string | null;
  @Input() public allowedTypes: string[] = [];
  @Input() public uploadToStorage = false;
  @Input() public showImagePreview = false;
  @Input() public showCsvPreview = false;

  protected readonly ButtonStyle = ButtonStyle;
  public selectedFile: File | null = null;
  public imagePreview: string | null = null;
  public selectedFileSize!: string | null;
  public isDragOver = false;
  public isUploading = false;

  @Output() public uploadedFileName = new EventEmitter<string | null>();
  @Output() public invalidTypeError = new EventEmitter<boolean>();
  @Output() public fileSelected = new EventEmitter<File | null>();

  @ViewChild('fileInput') public fileInput!: ElementRef;

  public ngOnInit(): void {
    if (this.fileName && this.showImagePreview) {
      this.generateUploadedImagePreview(this.fileName);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  public onDragLeave(): void {
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.selectedFile = event.dataTransfer.files[0];
      this.uploadFile();
    }
  }

  public triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFile = input.files[0] || null;
      this.fileName = this.selectedFile.name;
      if (this.validateFileType(this.selectedFile)) {
        this.invalidTypeError.emit(false);
        this.uploadFile();
      } else {
        this.invalidTypeError.emit(true);
      }
    }
  }

  public validateFileType(file: File): boolean {
    return this.allowedTypes.includes(file.type);
  }

  public uploadFile(): void {
    this.isUploading = true;
    this.selectedFileSize = this.selectedFile && getFileSize(this.selectedFile);

    if (this.uploadToStorage) {
      this.uploadFileToStorage();
    } else {
      this.emitFileUpload();
    }
  }

  public async uploadFileToStorage(): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    try {
      await this.blobUploadService.uploadFile(this.selectedFile);
      if (this.showImagePreview) {
        this.generateImagePreview(this.selectedFile);
      }
      this.fileName = this.selectedFile.name;
      this.uploadedFileName.emit(this.fileName);
      this.isUploading = false;
    } catch (error) {
      this.isUploading = false;
      this.fileName = null;
      console.error('Upload failed:', error);
      this.snackBar.openFromComponent(CustomSnackbarComponent, {
        data: {
          message: 'upload.error.uploadFailed',
        },
        duration: 5000,
        panelClass: ['error-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    }
  }

  public emitFileUpload(): void {
    if (!this.selectedFile) {
      return;
    }
    this.fileName = this.selectedFile.name;
    this.isUploading = false;
    this.fileSelected.emit(this.selectedFile);
  }

  public resetFileChanges(): void {
    this.fileName = null;
    this.selectedFile = null;
    this.fileSelected.emit(this.selectedFile);
  }

  public async deleteFile(): Promise<void> {
    if (!this.fileName) {
      return;
    }

    try {
      await this.blobUploadService.deleteFile(this.fileName!);
      this.imagePreview = null;
      this.fileName = null;
      this.uploadedFileName.emit(this.fileName);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }

  public generateImagePreview(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  private async generateUploadedImagePreview(fileName: string): Promise<void> {
    const url = this.blobUploadService.getUrlByName(fileName);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      this.imagePreview = URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching file:', error);
    }
  }
}
